import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Monitor, Download, Trash2, MailCheck, MailWarning, Copy, KeyRound } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Perfil() {
  const { user, refreshUser, setUser } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePwd, setDeletePwd] = useState("");
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  const loadSessions = () => api.get("/auth/sessions").then((r) => setSessions(r.data));
  useEffect(() => { loadSessions(); }, []);

  const start2FA = async () => {
    try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); } catch (e) { toast.error(apiError(e)); }
  };

  const enable2FA = async () => {
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      setRecoveryCodes(data.recovery_codes);
      setSetup(null); setCode("");
      await refreshUser();
      toast.success("2FA activado");
    } catch (e) { toast.error(apiError(e)); }
  };

  const disable2FA = async () => {
    try {
      await api.post("/auth/2fa/disable", { code });
      setDisableOpen(false); setCode("");
      await refreshUser();
      toast.success("2FA desactivado");
    } catch (e) { toast.error(apiError(e)); }
  };

  const revoke = async (sid) => { await api.delete(`/auth/sessions/${sid}`); loadSessions(); };

  const logoutAll = async () => {
    await api.post("/auth/logout-all");
    setUser(false);
    navigate("/login");
  };

  const exportData = async () => {
    const { data } = await api.get("/gdpr/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "radar-financiero-datos.json";
    a.click();
    toast.success("Datos exportados");
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/account", { data: { password: deletePwd } });
      toast.success("Cuenta eliminada");
      setUser(false);
      navigate("/login");
    } catch (e) { toast.error(apiError(e)); }
  };

  const resendVerification = async () => {
    const { data } = await api.post("/auth/resend-verification");
    toast.success(data.email_sent ? "Email de verificación enviado" : "El envío de emails aún no está configurado (falta la clave de Resend)");
  };

  const changePassword = async () => {
    if (!pwdForm.current || !pwdForm.next) return toast.error("Rellena ambos campos");
    if (pwdForm.next.length < 6) return toast.error("La nueva contraseña debe tener al menos 6 caracteres");
    if (pwdForm.next !== pwdForm.confirm) return toast.error("Las contraseñas no coinciden");
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwdForm.current, new_password: pwdForm.next });
      toast.success("Contraseña actualizada. Se han cerrado tus otras sesiones.");
      setPwdOpen(false);
      setPwdForm({ current: "", next: "", confirm: "" });
      loadSessions();
    } catch (e) { toast.error(apiError(e)); }
    setPwdBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="perfil-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Perfil y seguridad</h1>
        <p className="text-sm text-muted-foreground mt-1">{user?.email}</p>
      </div>

      {/* Verificación de email */}
      <div className="border border-border rounded-lg p-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          {user?.email_verified ? <MailCheck size={18} className="text-emerald-600" /> : <MailWarning size={18} className="text-amber-500" />}
          <div>
            <div className="text-sm font-bold">Verificación de email</div>
            <div className="text-xs text-muted-foreground">{user?.email_verified ? "Tu email está verificado." : "Email pendiente de verificar."}</div>
          </div>
        </div>
        {!user?.email_verified && (
          <Button data-testid="resend-verification-button" variant="outline" size="sm" onClick={resendVerification}>Reenviar verificación</Button>
        )}
      </div>

      {/* Cambio de contraseña */}
      <div className="border border-border rounded-lg p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="change-password-section">
        <div className="flex items-center gap-3">
          <KeyRound size={18} className="text-muted-foreground" />
          <div>
            <div className="text-sm font-bold">Contraseña</div>
            <div className="text-xs text-muted-foreground">Cámbiala periódicamente. Al cambiarla, se cierran las demás sesiones activas.</div>
          </div>
        </div>
        <Button data-testid="open-change-password" variant="outline" size="sm" onClick={() => setPwdOpen(true)}>Cambiar contraseña</Button>
      </div>

      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader><DialogTitle>Cambiar contraseña</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="block text-xs">
              <span className="text-muted-foreground">Contraseña actual</span>
              <Input type="password" value={pwdForm.current} onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })} data-testid="pwd-current" autoComplete="current-password" />
            </label>
            <label className="block text-xs">
              <span className="text-muted-foreground">Nueva contraseña (mínimo 6 caracteres)</span>
              <Input type="password" value={pwdForm.next} onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })} data-testid="pwd-new" autoComplete="new-password" />
            </label>
            <label className="block text-xs">
              <span className="text-muted-foreground">Repite la nueva contraseña</span>
              <Input type="password" value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} data-testid="pwd-confirm" autoComplete="new-password" />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => setPwdOpen(false)}>Cancelar</Button>
              <Button size="sm" onClick={changePassword} disabled={pwdBusy} data-testid="pwd-submit">Guardar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2FA */}
      <div className="border border-border rounded-lg p-5 space-y-4" data-testid="twofa-section">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {user?.totp_enabled ? <ShieldCheck size={18} className="text-emerald-600" /> : <ShieldOff size={18} className="text-muted-foreground" />}
            <div>
              <div className="text-sm font-bold">Doble factor de autenticación (2FA)</div>
              <div className="text-xs text-muted-foreground">Compatible con Google Authenticator, Microsoft Authenticator y Authy.</div>
            </div>
          </div>
          {user?.totp_enabled ? (
            <Button data-testid="disable-2fa-button" variant="outline" size="sm" onClick={() => setDisableOpen(true)}>Desactivar</Button>
          ) : (
            <Button data-testid="enable-2fa-button" size="sm" onClick={start2FA}>Activar 2FA</Button>
          )}
        </div>

        {setup && (
          <div className="border-t border-border pt-4 flex flex-col sm:flex-row gap-5 items-start" data-testid="twofa-setup">
            <img src={`data:image/png;base64,${setup.qr_base64}`} alt="QR 2FA" className="w-36 h-36 border border-border rounded-lg" />
            <div className="space-y-3 flex-1">
              <p className="text-sm">1. Escanea el QR con tu app Authenticator.<br />2. Introduce el código de 6 dígitos.</p>
              <p className="text-xs text-muted-foreground break-all">Clave manual: <code className="bg-secondary px-1 rounded">{setup.secret}</code></p>
              <div className="flex gap-2">
                <Input data-testid="twofa-enable-code-input" inputMode="numeric" placeholder="000000" value={code} onChange={(e) => setCode(e.target.value)} className="w-32 text-center tracking-widest" />
                <Button data-testid="twofa-enable-confirm" onClick={enable2FA} disabled={code.length < 6}>Confirmar</Button>
              </div>
            </div>
          </div>
        )}

        {recoveryCodes && (
          <div className="border-t border-border pt-4" data-testid="recovery-codes">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-bold">Códigos de recuperación — guárdalos ahora, no se volverán a mostrar</p>
              <button data-testid="copy-recovery-codes" onClick={() => { navigator.clipboard.writeText(recoveryCodes.join("\n")); toast.success("Códigos copiados"); }} className="text-muted-foreground hover:text-foreground"><Copy size={15} /></button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {recoveryCodes.map((c) => <code key={c} className="bg-secondary px-2 py-1.5 rounded text-center text-sm tracking-wider">{c}</code>)}
            </div>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => setRecoveryCodes(null)}>Ya los he guardado</Button>
          </div>
        )}
      </div>

      {/* Sesiones */}
      <div className="border border-border rounded-lg p-5" data-testid="sessions-section">
        <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
          <h3 className="font-heading font-bold text-lg">Sesiones activas ({sessions.length})</h3>
          <Button data-testid="logout-all-button" variant="outline" size="sm" onClick={logoutAll}>Cerrar sesión en todos los dispositivos</Button>
        </div>
        <div className="divide-y divide-border">
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center justify-between py-2.5 gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Monitor size={16} className="text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{s.user_agent?.split(")")[0]?.split("(")[1] || s.user_agent || "Dispositivo"} {s.current && <span className="text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded ml-1">Actual</span>}</div>
                  <div className="text-xs text-muted-foreground">Última actividad: {fmtDate(s.last_seen?.slice(0, 10))} · IP {s.ip || "—"}</div>
                </div>
              </div>
              {!s.current && (
                <button data-testid={`revoke-session-${s.id}`} onClick={() => revoke(s.id)} className="text-xs font-semibold text-muted-foreground hover:text-red-600">Revocar</button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* RGPD */}
      <div className="border border-border rounded-lg p-5 space-y-4" data-testid="gdpr-section">
        <h3 className="font-heading font-bold text-lg">Tus datos (RGPD)</h3>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="export-data-button" variant="outline" onClick={exportData}><Download size={15} className="mr-1.5" /> Exportar todos mis datos</Button>
          <Button data-testid="delete-account-button" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDeleteOpen(true)}><Trash2 size={15} className="mr-1.5" /> Eliminar mi cuenta</Button>
        </div>
      </div>

      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-heading">Desactivar 2FA</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Introduce un código de tu app o un código de recuperación.</p>
          <Input data-testid="twofa-disable-code-input" placeholder="Código" value={code} onChange={(e) => setCode(e.target.value)} className="text-center tracking-widest" />
          <Button data-testid="twofa-disable-confirm" onClick={disable2FA} disabled={!code} className="w-full">Desactivar</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-heading text-red-600">Eliminar cuenta</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Esta acción es irreversible. Si eres el único usuario, se eliminarán todos los datos de tu unidad familiar.</p>
          <Input data-testid="delete-account-password" type="password" placeholder="Tu contraseña" value={deletePwd} onChange={(e) => setDeletePwd(e.target.value)} />
          <Button data-testid="delete-account-confirm" onClick={deleteAccount} disabled={!deletePwd} className="w-full bg-red-600 hover:bg-red-700">Eliminar definitivamente</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
